// Main admin landing page

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Settings, FileText, LogOut, ArrowRight, Clock } from 'lucide-react';
import { NotificationBell } from '../components/NotificationBell';
import { auth } from '../../../config/firebase/auth';
import { useRestaurant } from '../../../providers/RestaurantContext';
import Logo from '../../../assets/fonts/Solo-logo.png';

const AdminDashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const { restaurant } = useRestaurant();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col px-6 py-6 animate-fade-in">
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-8 w-full relative">
        {/* Left Side - Logo */}
        <div className="flex items-center">
          <img 
            src={Logo} 
            alt="YAP Logo" 
            className="h-10 w-auto object-contain"
          />
        </div>

        {/* Center - Clock Display */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-zinc-900/40 backdrop-blur-xl border border-white/5 rounded-full">
          <Clock size={16} className="text-zinc-400" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-white leading-none">
              {currentTime.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="text-[10px] text-zinc-500 font-medium leading-none mt-0.5">
              {currentTime.toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
          </div>
        </div>

        {/* Right Side - Bell + Logout (no Volver a inicio; this is the inicio) */}
        <div className="flex items-center gap-4">
          <NotificationBell />
          <div className="w-px h-6 bg-zinc-700"></div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-red-500 transition-colors font-medium"
          >
            <LogOut size={18} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-[78vh] w-full max-w-4xl mx-auto px-6">
        {/* Restaurant Header */}
        <div className="text-center mb-10 space-y-4">
          {restaurant?.logoUrl && (
            <div className="flex justify-center mb-4">
              <img
                src={restaurant.logoUrl}
                alt={restaurant.name ? `Logo ${restaurant.name}` : 'Logo del restaurante'}
                className="h-16 md:h-20 w-auto object-contain max-w-[180px]"
              />
            </div>
          )}
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-2">
            {restaurant?.name || 'Panel de Administración'}
          </h1>
          
          {/* POS Status Badge */}
          {restaurant?.posConfig && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-900/50 border border-white/10 rounded-full backdrop-blur-md mb-2">
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${
                  restaurant.posConfig.enabled ? 'bg-yap-primary' : 'bg-zinc-600'
                } opacity-75`}></span>
                <span className={`relative inline-flex rounded-full h-2 w-2 ${
                  restaurant.posConfig.enabled ? 'bg-yap-primary' : 'bg-zinc-600'
                }`}></span>
              </span>
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">
                POS: <span className="text-white">{restaurant.posConfig.provider}</span>
              </span>
            </div>
          )}
          
          <p className="text-zinc-500 text-xs md:text-sm mt-2 font-medium max-w-md mx-auto">
            Elige una sección para continuar.
          </p>
        </div>

        {/* Grid Menu */}
        <div className="flex flex-col gap-4 w-full">
          {/* Mesas - Extra Large Featured Card */}
          <DashboardCard
            to="/admin/mesas"
            title="Mesas"
            subtitle="Abrir, ver y gestionar mesas en tiempo real."
            icon={<Table size={24} strokeWidth={2.5} />}
            isFeatured
          />

          {/* Bottom Row - Pagos and Configuración */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DashboardCard
              to="/admin/pagos"
              title="Pagos"
              subtitle="Transacciones y pagos aprobados."
              icon={<FileText size={20} strokeWidth={2} />}
            />

            <DashboardCard
              to="/admin/settings"
              title="Configuración"
              subtitle="Nombre, logo, menú y ajustes generales."
              icon={<Settings size={20} strokeWidth={2} />}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

type DashboardCardProps = {
  to: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  isFeatured?: boolean;
};

function DashboardCard({ to, title, subtitle, icon, isFeatured = false }: DashboardCardProps) {
  return (
    <Link
      to={to}
      className={`group relative flex flex-col p-4 w-full bg-zinc-900/40 hover:bg-zinc-800/60 backdrop-blur-xl rounded-xl border border-white/5 hover:border-yap-primary/30 transition-all duration-500 ease-out hover:shadow-[0_0_30px_-15px_rgba(0,255,132,0.15)] overflow-hidden ${
        isFeatured ? 'h-[180px]' : 'h-[140px]'
      }`}
    >
      {/* Hover Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-yap-primary/0 via-transparent to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-500" />
      
      {/* Content */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Top Section - Title and Icon */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className={`font-bold text-white mb-2 tracking-tight group-hover:text-yap-primary transition-colors ${
              isFeatured ? 'text-xl' : 'text-base'
            }`}>
              {title}
            </h3>
            <p className={`text-zinc-400 font-medium leading-relaxed group-hover:text-zinc-300 transition-colors ${
              isFeatured ? 'text-xs max-w-md' : 'text-[11px] max-w-[220px]'
            }`}>
              {subtitle}
            </p>
          </div>

          <div className={`rounded-lg bg-zinc-800/80 group-hover:bg-yap-primary text-zinc-400 group-hover:text-black flex items-center justify-center transition-all duration-300 shadow-inner group-hover:shadow-lg group-hover:scale-110 group-hover:rotate-3 ${
            isFeatured ? 'w-11 h-11' : 'w-8 h-8'
          }`}>
            {icon}
          </div>
        </div>

        {/* Bottom Section - Action Button */}
        <div className="mt-auto">
          <div className="flex items-center justify-center w-full py-2.5 px-3 bg-zinc-800/40 group-hover:bg-yap-primary/10 border border-white/5 group-hover:border-yap-primary/30 rounded-lg transition-all duration-300">
            <span className={`font-bold text-zinc-400 group-hover:text-yap-primary transition-colors ${
              isFeatured ? 'text-xs' : 'text-[11px]'
            }`}>
              Abrir
            </span>
            <ArrowRight className={`ml-2 text-zinc-400 group-hover:text-yap-primary transition-all duration-300 group-hover:translate-x-1 ${
              isFeatured ? 'w-3.5 h-3.5' : 'w-3 h-3'
            }`} />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default AdminDashboardHome;

